'use client';

import {
  ISalePageDetailV2Response,
  ISalePageDetailV2ResponseProduct,
  ISalePageProductAdditionalInformResponse,
} from '@/api/types';
import { Button, SummaryCTAWrapper } from '@/components';
import { useIsLoggedIn } from '@/entities';
import { useLoginInducingModal } from '@/hooks/useLoginInducingModal';
import { qaAttr, qaId } from '@/lib/qa';
import {
  AnimatedNumber,
  Column,
  Element,
  Icon,
  Row,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@vuddy/components';
import { ChangeEvent } from 'react';
import { useCart } from '../../../[salePageId]/contexts/CartContext';
import * as styles from '../common/bottom.css';
import { ConnectedVerificationCodeModal } from '../common/ConnectedVerificationCodeModal';
import { DigitalItemListDesktop } from './DigitalItemListDesktop';
import {
  DigitalProductSelectDropdown,
  SelectedDigitalProductList,
} from './DigitalProductSelectDropdown';
import { useDigitalPurchaseLogic } from './useDigitalPurchaseLogic';

interface IDigitalPurchaseProps {
  salePageId: number;
  data: ISalePageDetailV2Response;
}

export const DigitalPurchase = ({
  salePageId,
  data,
}: IDigitalPurchaseProps) => {
  const { isLoggedIn } = useIsLoggedIn();
  const loginConfirm = useLoginInducingModal();

  // 데스크톱은 CartProvider 안에서 렌더링되므로 useCart() 직접 호출 가능.
  const {
    selectedId: cartSelectedId,
    setSelectedId: setCartSelectedId,
    selectedProductIds,
    setSelectedProductIds,
  } = useCart();

  const {
    itemList,
    isClass,
    isAppWebView,
    selectedId,
    selectedIds,
    selectedProducts,
    totalSelectedAmount,
    setSelectedId,
    toggleSelectedId,
    verification,
    handleCTAClick,
    handleAddToCartClick: addToCart,
  } = useDigitalPurchaseLogic({
    salePageId,
    data,
    initialSelectedId: cartSelectedId,
    syncSelectedId: setCartSelectedId,
    initialSelectedIds: selectedProductIds,
    syncSelectedIds: setSelectedProductIds,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedId(Number(e.target.value));
  };

  const handleBuyNowClick = () => {
    if (!isLoggedIn) {
      loginConfirm();
      return;
    }
    handleCTAClick();
  };

  const handleAddToCartClick = () => {
    if (!isLoggedIn) {
      loginConfirm();
      return;
    }
    addToCart();
  };

  return (
    <Column
      {...qaAttr(qaId('sale.purchase', 'digital-sheet'))}
      className={styles.desktopWrapper}>
      {isAppWebView ? (
        <Element className={styles.desktopBottomSheetContents}>
          <DigitalItemListDesktop
            itemList={
              itemList as (ISalePageDetailV2ResponseProduct &
                ISalePageProductAdditionalInformResponse)[]
            }
            selectedId={selectedId || undefined}
            onChange={handleChange}
            onCodeInputClick={itemId => {
              verification.openVerificationModal(itemId);
            }}
            verifiedProductCodes={verification.verifiedProductCodes}
          />
        </Element>
      ) : (
        <Column className={styles.desktopWrapper}>
          <DigitalProductSelectDropdown
            itemList={
              itemList as (ISalePageDetailV2ResponseProduct &
                ISalePageProductAdditionalInformResponse)[]
            }
            selectedIds={selectedIds}
            onToggle={toggleSelectedId}
            onCodeInputClick={itemId => {
              verification.openVerificationModal(itemId);
            }}
            verifiedProductCodes={verification.verifiedProductCodes}
          />
          {selectedProducts.length > 0 && (
            <SelectedDigitalProductList
              items={
                selectedProducts as (ISalePageDetailV2ResponseProduct &
                  ISalePageProductAdditionalInformResponse)[]
              }
              onDelete={toggleSelectedId}
              onCodeInputClick={itemId => {
                verification.openVerificationModal(itemId);
              }}
              verifiedProductCodes={verification.verifiedProductCodes}
            />
          )}
        </Column>
      )}
      <SummaryCTAWrapper
        direction="vertical"
        position="sticky"
        description={
          <Row
            {...qaAttr(qaId('sale.purchase', 'total-amount'))}
            justifyContent="space-between">
            <Tooltip
              placement="top-start"
              offsetOptions={{ mainAxis: 10, crossAxis: -5 }}>
              <TooltipTrigger
                asChild
                {...qaAttr(qaId('sale.purchase', 'total-amount.trigger'))}>
                <Row alignItems="center" gap="gap_xs">
                  <Element typography="bodySmallAccent" color="secondary">
                    총 금액
                  </Element>
                  <Icon
                    name="FILL_INFORMATION_CIRCLE"
                    color="tertiary"
                    size="19px"
                  />
                </Row>
              </TooltipTrigger>
              <TooltipContent fullWidth>
                표시된 금액은 최종 금액이 아니며, 주문서에서 최종 금액을 확인해
                주세요.
              </TooltipContent>
            </Tooltip>
            <AnimatedNumber
              value={totalSelectedAmount}
              typography="titleBase"
              color="primary"
            />
          </Row>
        }>
        {isAppWebView ? (
          <Button
            {...qaAttr(qaId('sale.purchase', 'buy-now'))}
            size="large"
            fullWidth
            onClick={handleBuyNowClick}
            disabled={!selectedId}>
            {isClass ? '이용권 구매하기' : '바로 구매'}
          </Button>
        ) : (
          <Row gap="gap_small">
            <Element flex={1}>
              <Button
                {...qaAttr(qaId('sale.purchase', 'add-to-cart'))}
                variant="outlinePrimary"
                size="large"
                fullWidth
                onClick={handleAddToCartClick}
                disabled={selectedProducts.length === 0}>
                장바구니 추가
              </Button>
            </Element>
            <Element flex={1}>
              <Button
                {...qaAttr(qaId('sale.purchase', 'buy-now'))}
                size="large"
                fullWidth
                onClick={handleBuyNowClick}
                disabled={selectedProducts.length === 0}>
                {isClass ? '이용권 구매하기' : '바로 구매'}
              </Button>
            </Element>
          </Row>
        )}
      </SummaryCTAWrapper>
      <ConnectedVerificationCodeModal verification={verification} />
    </Column>
  );
};
